import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity } from 'react-native';
import { router } from 'expo-router';
import { useTheme, FONT_SIZES, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { Button, ErrorBanner, Input } from '../common';
import { useAuthStore } from '../../store/authStore';
import { useAuthGateStore } from '../../store/authGateStore';
import { useStructuresStore } from '../../store/structuresStore';
import { forwardGeocode, type ForwardGeocodeResult } from '../../utils/geocode';
import { UpgradeAccountSheet } from '../auth/UpgradeAccountSheet';
import { LocationPickerMap } from './LocationPickerMap';

export interface AddPropertyFormProps {
  /** Whether this view is the one currently showing — drives the
   *  reset-on-open effect below. Same "visible prop or fresh mount either
   *  way" reasoning as PlanMapImportForm: the AdaptiveSheet host keeps this
   *  mounted across opens, the desktop dock's PanelStack mounts a fresh
   *  instance every time. */
  visible: boolean;
  onClose: () => void;
  initialAddress?: string;
  /** The desktop dock (PropertyDockPanel.web.tsx) shows this form's title
   *  in its own pinned header instead (panel-system.md review, note 11) —
   *  set there so the body doesn't repeat it. The AdaptiveSheet host
   *  (AddPropertySheet, everywhere else) omits this; the body copy is its
   *  only heading. */
  hideTitle?: boolean;
}

type Step = 'address' | 'confirm';

/**
 * The body half of the add-property surface (panel-system.md Phase 2, same
 * split as DefensePlanAssignForm/Sheet): walks a user through adding a new
 * structure they automatically claim. Anonymous Fire Scouts get a sign-up
 * prompt first — adding to the shared dataset requires an email-backed
 * identity. AddPropertySheet.tsx hosts this in an AdaptiveSheet (profile,
 * the structure page, phone); the desktop map route docks it directly
 * instead (PropertyDockPanel.web.tsx).
 */
export function AddPropertyForm({
  visible,
  onClose,
  initialAddress,
  hideTitle = false,
}: AddPropertyFormProps) {
  const { colors } = useTheme();
  const user = useAuthStore((s) => s.user);
  const openAuthGate = useAuthGateStore((s) => s.open);
  const addUserStructure = useStructuresStore((s) => s.addUserStructure);

  const cityRef = useRef<TextInput>(null);
  const stateRef = useRef<TextInput>(null);
  const zipRef = useRef<TextInput>(null);

  const [step, setStep] = useState<Step>('address');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [centroid, setCentroid] = useState<{ latitude: number; longitude: number } | null>(null);
  const [geocodeResults, setGeocodeResults] = useState<ForwardGeocodeResult[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // Set when the user taps submit while still anonymous. The post-upgrade
  // effect reads this — using a ref instead of state ensures it survives
  // the re-render that flips isAnonymous to false without scheduling a
  // separate effect run.
  const pendingSubmitAfterUpgradeRef = useRef(false);

  const submitLabel = !user
    ? 'Sign in & add property'
    : user.isAnonymous
      ? 'Sign up & add property'
      : 'Add property';

  // Open-time form reset only — present/dismiss + the tab-bar hold live in
  // AdaptiveSheet now.
  useEffect(() => {
    if (visible) {
      setError(null);
      setStep('address');
      setAddress(initialAddress ?? '');
      setCity('');
      setState('');
      setZip('');
      setCentroid(null);
      setGeocodeResults([]);
    }
    pendingSubmitAfterUpgradeRef.current = false;
  }, [visible, initialAddress]);

  const applyGeocodeResult = useCallback((result: ForwardGeocodeResult) => {
    if (result.street) setAddress(result.street);
    if (result.city) setCity(result.city);
    if (result.state) setState(result.state);
    if (result.zip) setZip(result.zip);
    setCentroid(result.centroid);
    setStep('confirm');
  }, []);

  const handleGeocode = useCallback(async () => {
    const query = [address, city, state, zip].filter(Boolean).join(', ').trim();
    if (!query) {
      setError('Enter an address to continue.');
      return;
    }
    setError(null);
    setIsGeocoding(true);
    try {
      const results = await forwardGeocode(query);
      if (results.length === 0) {
        setError("We couldn't find that address. Double-check the spelling.");
        return;
      }
      setGeocodeResults(results);
      if (results.length === 1) {
        applyGeocodeResult(results[0]);
      }
    } catch {
      setError("Couldn't look up that address. Try again.");
    } finally {
      setIsGeocoding(false);
    }
  }, [address, city, state, zip, applyGeocodeResult]);

  const submit = useCallback(async () => {
    if (!centroid) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await addUserStructure({
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        zip: zip.trim() || undefined,
        centroid,
      });
      onClose();
      router.push(`/structure/${result.structureId}`);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message.replace(/^.*addUserStructure[^:]*:\s*/, '')
          : 'Could not add property.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [centroid, address, city, state, zip, onClose, addUserStructure]);

  const handleSubmit = useCallback(() => {
    // Signed-out → open the AuthGate (sign-in / sign-up / Continue as Fire
    // Scout). The effect below picks up the resulting user state and either
    // escalates to upgrade (if they picked the Fire Scout path) or submits.
    if (!user) {
      pendingSubmitAfterUpgradeRef.current = true;
      openAuthGate(
        () => {
          // No-op — the auth-state change re-runs the effect, which routes
          // the intent to upgrade-or-submit.
        },
        { message: 'Sign in or sign up to add a property' }
      );
      return;
    }
    if (user.isAnonymous) {
      pendingSubmitAfterUpgradeRef.current = true;
      setUpgradeOpen(true);
      return;
    }
    void submit();
  }, [user, openAuthGate, submit]);

  // Drives the post-auth continuation. Triggered when the user state changes
  // (after AuthGate or UpgradeAccountSheet succeeds) and the user previously
  // tapped submit. Strictly gated on the pending ref so unrelated state
  // changes never auto-fire a submission.
  useEffect(() => {
    if (
      !pendingSubmitAfterUpgradeRef.current ||
      upgradeOpen ||
      isSubmitting ||
      step !== 'confirm' ||
      !centroid
    ) {
      return;
    }
    if (!user) return; // AuthGate still open or user cancelled — sit tight.
    if (user.isAnonymous) {
      // They picked "Continue as Fire Scout" — escalate to the upgrade
      // sheet. The Cloud Function rejects anonymous callers, so a real
      // account is required either way.
      setUpgradeOpen(true);
      return;
    }
    pendingSubmitAfterUpgradeRef.current = false;
    void submit();
  }, [user, upgradeOpen, centroid, isSubmitting, step, submit]);

  const canContinue =
    address.trim().length > 0 && city.trim().length > 0 && state.trim().length > 0;

  const candidateRows = useMemo(() => geocodeResults.slice(0, 4), [geocodeResults]);

  return (
    <>
      <View
        testID="add-property-sheet"
        style={[styles.content, hideTitle && styles.contentInPanel]}>
        {!hideTitle && <Text style={[styles.title, { color: colors.text }]}>Add a property</Text>}
        <Text style={[styles.blurb, { color: colors.textSecondary }]}>
          {step === 'address'
            ? "Add your property so you can claim it and attach posts. We'll mark it as unverified until a reviewer confirms it."
            : 'Confirm the location on the map. Drag the pin to adjust if it landed in the wrong spot.'}
        </Text>

        {step === 'address' && (
          <>
            <Input
              compact
              label="Street address"
              value={address}
              onChangeText={setAddress}
              placeholder="123 Main St"
              autoCapitalize="words"
              autoCorrect={false}
              returnKeyType="next"
              onSubmitEditing={() => cityRef.current?.focus()}
              blurOnSubmit={false}
            />
            <Input
              compact
              ref={cityRef}
              label="City"
              value={city}
              onChangeText={setCity}
              placeholder="Big Bear Lake"
              autoCapitalize="words"
              returnKeyType="next"
              onSubmitEditing={() => stateRef.current?.focus()}
              blurOnSubmit={false}
            />
            <View style={styles.row}>
              <View style={styles.rowField}>
                <Input
                  compact
                  ref={stateRef}
                  label="State"
                  value={state}
                  onChangeText={setState}
                  placeholder="CA"
                  autoCapitalize="characters"
                  returnKeyType="next"
                  onSubmitEditing={() => zipRef.current?.focus()}
                  blurOnSubmit={false}
                />
              </View>
              <View style={styles.rowField}>
                <Input
                  compact
                  ref={zipRef}
                  label="ZIP"
                  value={zip}
                  onChangeText={setZip}
                  placeholder="92315"
                  keyboardType="number-pad"
                  returnKeyType="done"
                  onSubmitEditing={handleGeocode}
                />
              </View>
            </View>

            {candidateRows.length > 1 && (
              <View style={styles.candidates}>
                <Text style={[styles.candidatesHeader, { color: colors.textSecondary }]}>
                  Which one?
                </Text>
                {candidateRows.map((r, idx) => (
                  <TouchableOpacity
                    key={`${r.formattedAddress}-${idx}`}
                    onPress={() => applyGeocodeResult(r)}
                    style={[styles.candidate, { borderColor: colors.border }]}>
                    <Text style={[styles.candidateText, { color: colors.text }]} numberOfLines={2}>
                      {r.formattedAddress}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            <ErrorBanner message={error} />

            <Button
              title="Find on map"
              onPress={handleGeocode}
              loading={isGeocoding}
              disabled={!canContinue || isGeocoding}
              fullWidth
              size="sm"
            />
          </>
        )}

        {step === 'confirm' && centroid && (
          <>
            <View style={styles.summaryBlock}>
              <Text style={[styles.summaryLine, { color: colors.text }]}>{address}</Text>
              <Text style={[styles.summaryLineSecondary, { color: colors.textSecondary }]}>
                {city}, {state}
                {zip ? ` ${zip}` : ''}
              </Text>
            </View>

            <LocationPickerMap centroid={centroid} onChange={setCentroid} />

            <ErrorBanner message={error} />

            <View style={styles.confirmActions}>
              <Button
                title="Edit address"
                onPress={() => setStep('address')}
                variant="secondary"
                size="sm"
                style={styles.confirmAction}
              />
              <Button
                title={submitLabel}
                onPress={handleSubmit}
                loading={isSubmitting}
                size="sm"
                style={styles.confirmAction}
              />
            </View>
          </>
        )}
      </View>

      <UpgradeAccountSheet visible={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
    gap: SPACING.smd,
  },
  // In the docked panel (hideTitle): the PanelHeader's side padding, so the
  // text and fields line up under its title.
  contentInPanel: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  blurb: {
    ...TYPOGRAPHY.bodySmall,
    lineHeight: 20,
    marginBottom: SPACING.sm,
  },
  row: {
    flexDirection: 'row',
    gap: SPACING.smd,
  },
  rowField: { flex: 1 },
  candidates: {
    gap: SPACING.xs,
  },
  candidatesHeader: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.xs,
  },
  candidate: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.smd,
  },
  candidateText: {
    ...TYPOGRAPHY.bodySmall,
  },
  summaryBlock: {
    gap: 2,
  },
  summaryLine: {
    ...TYPOGRAPHY.h4,
    fontSize: FONT_SIZES.md,
  },
  summaryLineSecondary: {
    ...TYPOGRAPHY.bodySmall,
  },
  // The equal sm pair every panel footer uses (Sept 28).
  confirmActions: {
    flexDirection: 'row',
    gap: SPACING.sm,
  },
  confirmAction: {
    flex: 1,
  },
});
